/** The rover: one mission at a time, timed on the game clock (GDD §3.11). */

import { ROVER_FIND_CHANCE, ROVER_MISSIONS, ROVER_SCANNING_BONUS } from '../content/rovers';
import { roomDef } from '../content/rooms';
import { grantProspector } from './planets';
import { hasProject } from './projects';
import { nextRandom } from './rng';
import { roomCut, roomLevel } from './rooms';
import type { Emit } from './events';
import type { GameState } from './state';

export function missionAvailable(state: GameState, mission: number): boolean {
  const m = ROVER_MISSIONS[mission];
  return !!m && roomLevel(state, roomDef('robotics').id) >= m.robotics;
}

/** Seconds a mission takes after the Robotics room's cut. */
export function missionSeconds(state: GameState, mission: number): number {
  return ROVER_MISSIONS[mission]!.hours * 3600 * (1 - roomCut(state, 'roverTime'));
}

export function canLaunchRover(state: GameState, planet: number): boolean {
  return !state.run.challenge && hasProject(state, 'rover') && !state.rover && !!state.run.planets[planet]?.owned;
}

export function launchRover(state: GameState, planet: number, mission: number): boolean {
  if (!canLaunchRover(state, planet) || !missionAvailable(state, mission)) return false;
  state.rover = { planet, mission, endsAt: state.clock + missionSeconds(state, mission) };
  return true;
}

/** The planet being scanned, while a mission runs. */
export function scanningPlanet(state: GameState): number | null {
  return state.rover ? state.rover.planet : null;
}

export function stepRover(state: GameState, emit: Emit): void {
  const r = state.rover;
  if (!r || state.clock < r.endsAt) return;
  const sc = ROVER_MISSIONS[r.mission]!.sc;
  state.sc += sc;
  const chance = ROVER_FIND_CHANCE + (hasProject(state, 'rover_scanning') ? ROVER_SCANNING_BONUS : 0);
  const found = nextRandom(state) < chance ? grantProspector(state) : null;
  state.rover = null;
  emit({ kind: 'rover', sc, prospector: found });
}
